import * as path from "path";
import * as npmUtil from "@pagedip/util-npm";
import log from "@pagedip/util-logger";
import * as fs from "mz/fs";
import * as child_process from "mz/child_process";
import gitUrlParse from "git-url-parse";
import { Project, batchPackages } from "@pagedip/util-lerna";
import { URL } from "url";
import { matchBranch, BranchMatcher, coerceArray, isNonEmptyString, createTemplate } from "./util";
import { git as gitExec, gitHead, gitBranch, dargs } from "./git";
import { Package } from "./package";
import { Release } from "./release";

export namespace Env {
  export interface ResolveOptions {
    basedir?: string;
    dryrun?: boolean;
    branch?: BranchMatcher | BranchMatcher[];
    registryUrl?: string;
    publishRegistryUrl?: string;
    progress?: (pkg: Lerna.Package, total: number) => any;
    gitRemoteName?: string;
    gitTagTemplates?: string[];
    skipBuild?: boolean;
    npmAccess?: boolean;
    npmDistTags?: boolean;
  }

  export interface Git {
    branch?: string;
    head: string;
    tagTemplates: string[];
    remote?: gitUrlParse.GitUrl;
    skipBuild?: boolean;
  }

  export interface NPM {
    package: npmUtil.Package;
    registryUrl: string;
    publishRegistryUrl?: string;
    access?: boolean;
    distTags?: boolean;
  }

  export interface Lerna {
    project: Lerna.Project;
    packages: Lerna.Package[];
  }

  export interface CI {
    name: string;
    pullRequest: boolean;
  }
}

const npmRegistryHost = /^registry\.npmjs\.(?:org|com)$/i;

export class Env {
  static async resolveNpm(basedir: string, pkg: npmUtil.Package, options: Env.ResolveOptions = {}): Promise<Env.NPM> {
    // get the non-publish registry URL, as we may be reading from a different registry than publishing
    const registryUrl = options.registryUrl || npmUtil.getRegistry(pkg.name, basedir) || npmUtil.defaultRegistryUrl;

    // get the publish URL
    const publishUrl = options.publishRegistryUrl || pkg.publishConfig?.registry;

    // determine features available for the publish registry
    const isNpmRegistry = npmRegistryHost.test(new URL(publishUrl || registryUrl).host);
    const access = options.npmAccess ?? (isNpmRegistry ? true : undefined);
    const distTags = options.npmDistTags ?? (isNpmRegistry ? true : undefined);

    // build npm object
    return {
      registryUrl,
      publishRegistryUrl: publishUrl,
      package: pkg,
      access,
      distTags,
    };
  }

  static async resolveTravisCI(): Promise<Env.CI | undefined> {
    const { TRAVIS, CI, GH_TOKEN, TRAVIS_TAG, TRAVIS_PULL_REQUEST, TRAVIS_REPO_SLUG, TRAVIS_BRANCH } = process.env;

    // check we are running in Travis CI
    const travis = TRAVIS === "true" && CI === "true";
    if (!travis) return;

    // refuse to run if this run triggered by a tag
    if (TRAVIS_TAG) {
      throw new Error(`This release was triggered by a git tag.`);
    }

    // there must be a gitHub token if we are running in travis
    if (!GH_TOKEN) {
      throw new Error("Missing GH_TOKEN env variable");
    }

    // detect pull request
    const pullRequest = TRAVIS_PULL_REQUEST !== "false";

    // unshallow the git repo since travis only grabs the last 50 commits
    try {
      await gitExec("fetch", { unshallow: true });
    } catch (e) {
      // eat errors from the fetch
    }

    // when not in a pull request we need to configure the repo for release
    if (!pullRequest) {
      // add mandatory user config
      await gitExec("config", { local: true, _: ["user.email", "robot@pagedip.com"] });
      await gitExec("config", { local: true, _: ["user.name", "Autorelease Bot"] });

      // set the authenticated git remote url
      await gitExec("remote", ["set-url", "origin", `https://${GH_TOKEN}@github.com/${TRAVIS_REPO_SLUG}.git`]);

      // travis puts the repo into a detached head state, but we need to be on a branch to push
      await gitExec("checkout", [TRAVIS_BRANCH]);
    }

    return {
      name: "Travis CI",
      pullRequest,
    };
  }

  static async resolveGit(basedir: string, pkg: npmUtil.Package, options: Env.ResolveOptions = {}): Promise<Env.Git> {
    // get the name of the git branch
    const branch = process.env.TRAVIS_BRANCH || (await gitBranch(basedir));

    // get the current git head ref
    const head = await gitHead(basedir);

    // get tag templates
    const tagTemplates =
      options.gitTagTemplates == null
        ? ["{{ package && package.version.next }}"]
        : coerceArray(options.gitTagTemplates, isNonEmptyString);

    let origin: string | undefined;

    if (
      pkg?.repository &&
      typeof pkg.repository === "object" &&
      typeof pkg.repository.type === "string" &&
      pkg.repository.type === "git"
    ) {
      origin = pkg.repository.url;
    }

    if (!origin) {
      origin = await gitExec("remote", ["get-url", options.gitRemoteName ?? "origin"], { cwd: basedir });
    }

    const remote = origin ? gitUrlParse(origin) : undefined;

    return {
      branch,
      head,
      tagTemplates,
      remote,
      skipBuild: options.skipBuild !== false,
    };
  }

  static async resolveLerna(project: Lerna.Project): Promise<Env.Lerna | undefined> {
    if (!(await fs.exists(project.rootConfigLocation))) return;

    const packages = await project.getPackages();

    return {
      project,
      packages,
    };
  }

  static async resolveId(basedir: string) {
    const tags = await gitExec("tag", { list: true, _: ["release/*"] }, { cwd: basedir });

    const releases = tags
      .split(/\r?\n/g)
      .map((l) => parseInt(l.replace(/^release\//, ""), 10))
      .filter((n) => !isNaN(n) && n > 0)
      .sort((a, b) => a - b);

    return (releases[releases.length - 1] || 0) + 1;
  }

  static async resolvePackages(
    basedir: string,
    npm: Env.NPM,
    release: Release,
    lerna?: Env.Lerna,
    progress?: (pkg: Lerna.Package, total: number) => any
  ) {
    const getRegistryUrl = (p: npmUtil.Package) => npmUtil.getRegistry(p.name, basedir) || npmUtil.defaultRegistryUrl;

    if (lerna) {
      return await Package.loadLernaGraph(lerna.packages, {
        defaultRelease: release,
        progress,
        basedir,
        getRegistryUrl,
      });
    }

    const pkgjson = npm.package;
    const info = await npmUtil.fetchPackageInfo(getRegistryUrl(pkgjson), pkgjson.name);

    // build package object
    const pkg = new Package(basedir, pkgjson, release, info);

    // resolve all commits since the last release
    await pkg.resolveCommits({ basedir });

    // validate the package
    pkg.validate();

    return new Map([[pkg.name, pkg]]);
  }

  static async resolve(options?: Env.ResolveOptions) {
    options = options || {};

    // lerna project will give us what we need even if this isn't a lerna repo
    const project = new Project(options.basedir);
    const basedir = project.rootPath;
    const pkg = project.manifest.toJSON() as npmUtil.Package;

    // resolve all the parts
    const [release, id, npm, git, lerna] = await Promise.all<Release, number, Env.NPM, Env.Git, Env.Lerna | undefined>([
      Release.load(basedir),
      Env.resolveId(basedir),
      Env.resolveNpm(basedir, pkg, options),
      Env.resolveGit(basedir, pkg, options),
      Env.resolveLerna(project),
    ]);

    const ci = await Env.resolveTravisCI();
    const packages = await Env.resolvePackages(basedir, npm, release, lerna, options.progress);

    const dryrun = Boolean(options.dryrun) || ci == null || ci.pullRequest || !matchBranch(options.branch, git.branch);

    return new Env(id, dryrun, basedir, packages, release, git, npm, lerna, ci);
  }

  logger = log.prefix("autorelease");

  constructor(
    public id: number,
    public dryrun: boolean,
    public basedir: string,
    public packages: Map<string, Package>,
    public release: Release,
    public git: Env.Git,
    public npm: Env.NPM,
    public lerna?: Env.Lerna,
    public ci?: Env.CI
  ) {}

  get package() {
    return this.lerna ? undefined : this.packages.values().next().value;
  }

  get gitTags() {
    const tags: string[] = [];

    for (const tpl of this.git.tagTemplates) {
      const tag = createTemplate(tpl)(this);
      if (tag) tags.push(tag);
    }

    return tags;
  }

  async safe(fn: (this: this) => any) {
    if (this.dryrun) return false;
    await fn.call(this);
    return true;
  }

  async gitExec(
    cmd: string,
    args?: string[] | Record<string, any>,
    options: child_process.ExecFileOptions & { safe?: boolean } = {}
  ) {
    const { safe, ...opts } = options;
    const execOpts = { cwd: this.basedir, ...opts };
    let result: string | undefined;

    if (safe) {
      await this.safe(async () => {
        result = await gitExec(cmd, args, execOpts);
      });
    } else {
      result = await gitExec(cmd, args, execOpts);
    }

    this.logger.debug(
      `git ${cmd} ${dargs(args)
        .map((m) => JSON.stringify(m))
        .join(" ")}`
    );
    return result;
  }

  safeGitExec(cmd: string, args?: string[] | Record<string, any>, options?: child_process.ExecFileOptions) {
    return this.gitExec(cmd, args, { ...options, safe: true });
  }

  async prepare() {
    let atLeastOneRelease = false;
    for (const pkg of this.packages.values()) {
      const persisted = await pkg.persistChangelog(this);
      atLeastOneRelease = atLeastOneRelease || persisted;
    }

    if (!atLeastOneRelease) return false;

    // reset shared RELEASE.yml but only if this is a lerna project
    if (this.lerna != null) {
      const releaseFiles = await Release.findFiles(this.basedir);
      const changedFiles: string[] = [];

      for (const filePath of releaseFiles) {
        if (filePath !== this.release.filePath) {
          changedFiles.push(filePath);
          await this.safe(async () => fs.unlink(filePath));
          this.logger.debug("unlink %s", log.color.blue(path.relative(this.basedir, filePath)));
        }
      }

      this.release.reset().write({
        basedir: this.basedir,
        dryrun: this.dryrun,
        debug: this.logger.debug,
      });

      changedFiles.push(this.release.filePath);
      await this.safeGitExec(
        "add",
        changedFiles.map((f) => path.relative(this.basedir, f))
      );
    }

    const singlePackage = this.package;
    const id = singlePackage && singlePackage.version.next ? singlePackage.version.next : "" + this.id;

    let commitMessage = `release ${id} [autorelease]`;
    if (this.git.skipBuild) commitMessage += " [skip ci]";

    await this.safeGitExec("commit", {
      message: commitMessage,
      allowEmpty: true,
    });

    // update the git head so package publish use the correct commit id
    this.git.head = await gitHead(this.basedir);

    let message = "";
    let count = 0;
    for (const p of this.packages.values()) {
      if (p.canRelease && p.version.next) {
        message += `\n${p.name}@${p.version.next}`;
        count++;
      }
    }
    message = `${count} package${count !== 1 ? "s" : ""}` + message;

    await this.safeGitExec("tag", {
      annotate: true,
      message,
      _: [`release/${this.id}`],
    });

    for (const tag of this.gitTags) {
      await this.safeGitExec("tag", {
        annotate: true,
        message: tag,
        _: [tag],
      });
    }

    if (this.git.branch) {
      await this.safeGitExec("push", ["origin", "master"]);
      await this.safeGitExec("push", { tags: true });
    }

    return true;
  }

  async publish() {
    const published: Package[] = [];

    // publish all packages that should be published
    if (this.lerna) {
      const batches = batchPackages(this.lerna.packages, false, "dependencies");

      for (const batch of batches) {
        for (const { name } of batch) {
          const pkg = this.packages.get(name);
          if (pkg && (await pkg.publish(this))) published.push(pkg);
        }
      }
    } else {
      for (const pkg of this.packages.values()) {
        if (await pkg.publish(this)) published.push(pkg);
      }
    }

    // move the dist-tag for everything that was published
    for (const pkg of published) {
      await pkg.switchReleaseDistTag(this);
    }

    return published;
  }
}
